import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";

import Logo from "src/components/common/Logo";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import AuthBrandPanel from "src/components/auth/AuthBrandPanel";
import AuthThemeToggle from "src/components/auth/AuthThemeToggle";

import { getFromStorage, setToStorage } from "src/utils/storage";
import storageKeys from "src/constants/storage";
import * as authTenantActions from "src/redux/slices/authTenantSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { login } = useAppSelector((state) => state.authTenant);

  useEffect(() => {
    const authTenantToken = getFromStorage(storageKeys.authTenantToken);
    if (authTenantToken) {
      navigate("/app/dashboard");
    }
  }, []);

  useEffect(() => {
    if (login?.data?.token) {
      setToStorage(storageKeys.authTenantToken, login.data.token);
      dispatch(authTenantActions.getUserDetailsRequest());
      navigate("/app/dashboard");
    } else if (login?.error) {
      setApiError(login.error);
    }
  }, [login, navigate]);

  const handleSubmit = (data: { email: string; password: string }) => {
    setApiError("");
    dispatch(authTenantActions.loginRequest(data));
  };

  const isPending = login?.errorCode === "PENDING_REVIEW";

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      <AuthThemeToggle />
      <AuthBrandPanel
        badge="Tenant workspace"
        heading="Every shelf, every SKU, one dashboard."
        subtext="Sign in to track stock, fulfil orders and keep every warehouse in sync — in real time."
      >
        <div className="flex gap-2.5">
          <div className="flex-1 rounded-xl border border-white/15 bg-white/10 p-3 backdrop-blur">
            <div className="text-[17px] font-extrabold tabular-nums tracking-tight">2,480+</div>
            <div className="text-[11px] text-white/65">SKUs tracked</div>
          </div>
          <div className="flex-1 rounded-xl border border-white/15 bg-white/10 p-3 backdrop-blur">
            <div className="text-[17px] font-extrabold tabular-nums tracking-tight">36</div>
            <div className="text-[11px] text-white/65">Locations live</div>
          </div>
          <div className="flex-1 rounded-xl border border-white/15 bg-white/10 p-3 backdrop-blur">
            <div className="text-[17px] font-extrabold tabular-nums tracking-tight">99.9%</div>
            <div className="text-[11px] text-white/65">Sync uptime</div>
          </div>
        </div>
      </AuthBrandPanel>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-[380px]">
          <div className="rounded-[18px] border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-5 flex items-center gap-2">
              <Logo size="small" variant="contained" />
              <Logo size="large" variant="text" />
            </div>

            <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
              Sign in to your workspace
            </h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              Use the email and password set up for you by your admin.
            </p>

            {isPending ? (
              <div className="mb-4 flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3.5 dark:border-amber-900/50 dark:bg-amber-900/20">
                <AlertTriangle
                  size={18}
                  className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
                />
                <div>
                  <p className="text-sm font-bold text-amber-800 dark:text-amber-300">
                    Your request is under review
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-amber-700/90 dark:text-amber-400/80">
                    {apiError}
                  </p>
                </div>
              </div>
            ) : (
              apiError && (
                <div className="mb-4 rounded-lg bg-red-100 p-2.5 text-center text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  {apiError}
                </div>
              )
            )}

            <LoginFields onSubmit={handleSubmit} loading={login?.loading} />

            <div className="my-5 flex items-center gap-2.5 text-xs text-gray-400 dark:text-gray-500">
              <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              new here
              <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              Don't have a workspace?{" "}
              <Link
                to="/app/register"
                className="font-semibold text-primary-600 hover:underline dark:text-primary-400"
              >
                Create your organization
              </Link>
            </p>

            <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              <ShieldCheck size={12} />
              Secured workspace access
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoginFields: React.FC<{
  onSubmit: (data: { email: string; password: string }) => void;
  loading?: boolean;
}> = ({ onSubmit, loading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form
      className="space-y-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ email, password });
      }}
    >
      <Input
        label="Email or username"
        type="email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        startAdornment={<Mail size={16} />}
        required
      />
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Password <span className="text-red-600">*</span>
          </label>
          <Link
            to="/app/forgot-password"
            className="text-xs font-semibold text-primary-600 hover:underline dark:text-primary-400"
          >
            Forgot password?
          </Link>
        </div>
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="••••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          startAdornment={<Lock size={16} />}
          endAdornment={
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="flex items-center"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
          required
        />
      </div>
      <Button type="submit" fullWidth isLoading={loading} className="mt-1.5">
        Sign in
        <ArrowRight size={15} className="ml-1.5" />
      </Button>
    </form>
  );
};

export default LoginPage;
